import { ErrorBoundary } from '@mc/error-boundary'
import Experiment from '@mc/experiments-react'
import { usePrimaryCta } from '@mc/growth'
import Nav from '@mc/navigation'
import { paths } from '@mc/paths'
import { useStaticData } from '@mc/static-data-react'
import { Product } from '@mc/track-event'
import CTA from 'components/CTA'
import { AdminLink } from 'components/Navigation/shared/AdminLink'
import { AtWorkLink } from 'components/Navigation/shared/AtWorkLink'
import { CertificatesLink } from 'components/Navigation/shared/CertificatesLink'
import { HideIfPathMatches } from 'components/Navigation/shared/HideIfPathMatches'
import { LoginMenu } from 'components/Navigation/shared/LoginMenu'
import MobileMenu from 'components/Navigation/shared/Mobile/Menu'
import { NavTitle } from 'components/Navigation/shared/NavTitle'
import { PlansLink } from 'components/Navigation/shared/PlansLink'
import { UserState } from 'components/UserState'
import {
  MCCERTS_NAVIGATION_TESTING_PRE_PAYWALL_EXP_FLAG,
  MobileMenuVariant1,
  MobileMenuVariant2,
  MobileMenuVariant3,
  TopNavigation,
} from 'experiments/mccerts-280_v1'
import { Browse } from './components/Browse'
import { GiftsLink } from './components/GiftsLink'
import { Search } from './components/Search'
import { SubcategoryBrowsingDropdownContent } from './components/SubcategoryBrowse'
import { trackSignUpCTAClick } from './events'
import { NavProvider, useSearchInputWidth } from './navContext'
import classes from './SubcategoryNavigation.module.scss'

const HIDE_SIGNUP_CTA_PATHS = [
  paths.checkout,
  paths.classRecommendations, // also matches quiz results as path prefix
  paths.bogo,
  paths.promotion,
]

export const SubcategoryNavigation = ({ appUser }: { appUser?: boolean }) => {
  const { isLoggedIn } = useStaticData<{ isLoggedIn: boolean }>()

  if (isLoggedIn && appUser === false) {
    return (
      <Nav
        background='solid'
        className={`mc-page__header mc-theme-dark ${classes.navHeader}`}
        navChildClassName='flex-nowrap'
      >
        <NavTitle className={classes.compact} />
        <Nav.Left
          className={`flex-grow-1 flex-shrink-1 justify-content-sm-start justify-content-between ${classes.compact}`}
        >
          <ErrorBoundary>
            <NavBrowseComponent />
          </ErrorBoundary>
          <SignUpCTA nonMember className='d-lg-none mc-ml-sm-5' />
        </Nav.Left>
        <Nav.Right>
          <UserState nonMember>
            <AtWorkLink className='d-none d-lg-block' />
          </UserState>
          <UserState nonMember>
            <HideIfPathMatches paths={[paths.checkout, paths.articles]}>
              <GiftsLink />
              <PlansLink />
            </HideIfPathMatches>
          </UserState>
          <UserState enterpriseAdmin>
            <AdminLink className='mc-ml-4' />
          </UserState>
          <LoginMenu>
            <MobileMenu />
          </LoginMenu>
          <SignUpCTA className='d-none d-lg-block mc-ml-4' />
        </Nav.Right>
      </Nav>
    )
  }

  return (
    // control is the existing core menu at the root path /
    // variant_1 is the existing core menu with a link to Certificates and a mobile menu
    // variant_2 has the top navigation without OnCall link and a new mobile menu
    // variant_3 has the top navigation with OnCall link and a new mobile menu
    <Experiment
      name={MCCERTS_NAVIGATION_TESTING_PRE_PAYWALL_EXP_FLAG}
      control={() => (
        <Nav
          background='solid'
          className={`mc-page__header mc-theme-dark ${classes.navHeader}`}
          navChildClassName='flex-nowrap'
        >
          <NavTitle className={classes.compact} />
          <Nav.Left
            className={`flex-grow-1 flex-shrink-1 justify-content-sm-start justify-content-between ${classes.compact}`}
          >
            <ErrorBoundary>
              <NavBrowseComponent />
            </ErrorBoundary>
            <SignUpCTA nonMember className='d-lg-none mc-ml-sm-5' />
          </Nav.Left>
          <Nav.Right>
            <UserState nonMember>
              <AtWorkLink className='d-none d-lg-block' />
            </UserState>
            <UserState nonMember>
              <HideIfPathMatches paths={[paths.checkout, paths.articles]}>
                <GiftsLink />
                <PlansLink />
              </HideIfPathMatches>
            </UserState>
            <UserState enterpriseAdmin>
              <AdminLink className='mc-ml-4' />
            </UserState>
            <LoginMenu>
              <MobileMenu />
            </LoginMenu>
            <SignUpCTA className='d-none d-lg-block mc-ml-4' />
          </Nav.Right>
        </Nav>
      )}
      variant_1={() => (
        <Nav
          background='solid'
          className={`mc-page__header mc-theme-dark ${classes.navHeader}`}
          navChildClassName='flex-nowrap'
        >
          <NavTitle className={classes.compact} />
          <Nav.Left
            className={`flex-grow-1 flex-shrink-1 justify-content-sm-start justify-content-between ${classes.compact}`}
          >
            <ErrorBoundary>
              <NavBrowseComponent />
            </ErrorBoundary>
            <SignUpCTA nonMember className='d-lg-none mc-ml-sm-5' />
          </Nav.Left>
          <Nav.Right>
            <UserState nonMember>
              <CertificatesLink className='d-none d-lg-block' />
            </UserState>
            <UserState nonMember>
              <AtWorkLink className='d-none d-lg-block' />
            </UserState>
            <UserState nonMember>
              <HideIfPathMatches paths={[paths.checkout, paths.articles]}>
                <GiftsLink />
                <PlansLink />
              </HideIfPathMatches>
            </UserState>
            <UserState enterpriseAdmin>
              <AdminLink className='mc-ml-4' />
            </UserState>
            <LoginMenu>
              <MobileMenuVariant1 />
            </LoginMenu>
            <SignUpCTA className='d-none d-lg-block mc-ml-4' />
          </Nav.Right>
        </Nav>
      )}
      variant_2={() => (
        <>
          <TopNavigation contained showAtWorkLink />
          <Nav
            background='solid'
            className={`mc-page__header mc-theme-dark ${classes.navHeader}`}
            navChildClassName='flex-nowrap'
          >
            <NavTitle className={classes.compact} />
            <Nav.Left
              className={`flex-grow-1 flex-shrink-1 justify-content-sm-start justify-content-between ${classes.compact}`}
            >
              <ErrorBoundary>
                <NavBrowseComponent />
              </ErrorBoundary>
              <SignUpCTA nonMember className='d-lg-none mc-ml-sm-5' />
            </Nav.Left>
            <Nav.Right>
              <UserState nonMember>
                <HideIfPathMatches paths={[paths.checkout, paths.articles]}>
                  <GiftsLink />
                  <PlansLink />
                </HideIfPathMatches>
              </UserState>
              <UserState enterpriseAdmin>
                <AdminLink className='mc-ml-4' />
              </UserState>
              <LoginMenu>
                <MobileMenuVariant2 />
              </LoginMenu>
              <SignUpCTA className='d-none d-lg-block mc-ml-4' />
            </Nav.Right>
          </Nav>
        </>
      )}
      variant_3={() => (
        <>
          <TopNavigation
            contained
            showOnCallLink
            showAtWorkLink
            product={Product.MASTERCLASS}
          />
          <Nav
            background='solid'
            className={`mc-page__header mc-theme-dark ${classes.navHeader}`}
            navChildClassName='flex-nowrap'
          >
            <NavTitle className={classes.compact} />
            <Nav.Left
              className={`flex-grow-1 flex-shrink-1 justify-content-sm-start justify-content-between ${classes.compact}`}
            >
              <ErrorBoundary>
                <NavBrowseComponent />
              </ErrorBoundary>
              <SignUpCTA nonMember className='d-lg-none mc-ml-sm-5' />
            </Nav.Left>
            <Nav.Right>
              <UserState nonMember>
                <HideIfPathMatches paths={[paths.checkout, paths.articles]}>
                  <GiftsLink />
                  <PlansLink />
                </HideIfPathMatches>
              </UserState>
              <UserState enterpriseAdmin>
                <AdminLink className='mc-ml-4' />
              </UserState>
              <LoginMenu>
                <MobileMenuVariant3 />
              </LoginMenu>
              <SignUpCTA className='d-none d-lg-block mc-ml-4' />
            </Nav.Right>
          </Nav>
        </>
      )}
    />
  )
}

const NavBrowseComponent = () => {
  const { searchInputRef, width } = useSearchInputWidth()

  return (
    <NavProvider width={width}>
      <Browse>
        <SubcategoryBrowsingDropdownContent />
      </Browse>
      <div ref={searchInputRef} className='d-none d-sm-flex flex-grow-1'>
        <Search />
      </div>
    </NavProvider>
  )
}

const SignUpCTA = ({
  className,
  nonMember,
}: {
  className: string
  nonMember?: boolean
}) => {
  const { ctaCopy } = usePrimaryCta()

  return (
    <UserState loggedOut nonMember={nonMember}>
      <HideIfPathMatches paths={HIDE_SIGNUP_CTA_PATHS}>
        <CTA
          className={className}
          onClick={trackSignUpCTAClick}
          trackProps={{
            action: `Header Nav Purchase CTA:${ctaCopy}`,
            level: 'primary',
            location: 'navigation',
          }}
        />
      </HideIfPathMatches>
    </UserState>
  )
}
